import format from 'date-fns/format'
import * as uuid from 'uuid'
import { AddressType, OpenTimeType, HandleOpenTimeType } from 'types/store'
import { StockType } from 'types/stock'
import { SpecialHolidayType } from 'types/calendar'
import { CheckoutItemType } from 'types/checkoutItem'
import { DayOfWeekType, DaysOfWeek } from './daysOfWeek'
import { FirebaseDocRefType, FirebaseTimestampType } from 'firebase/index'
import { formatISO, isAfter, isBefore } from 'date-fns'
import { CampaignStatus } from 'types/campaign'
import { FrozenDeliveryType } from 'types/storeDeliveryBread'
import { BreadType } from '../types/bread'
import { AsYouType } from 'libphonenumber-js'
const daysOfWeek = new DaysOfWeek()

// Date -> yyyyMMdd
export const dateToString = (date: Date): string => {
  return format(date, 'yyyyMMdd')
}

export const getRandomID = () => uuid.v4()

// yyyyMMdd -> Date
export const parseToDate = (date: string): Date => {
  return new Date(Number(date.slice(0, 4)), Number(date.slice(4, 6)) - 1, Number(date.slice(6, 8)))
}

// yyyyMMdd -> yyyy-MM-dd
export const parseDateWithHyphen = (date: string): string => {
  return format(parseToDate(date), 'yyyy-MM-dd')
}

// time -> hh:mm
export const parseTimeToDate = (time: string, date?: Date): Date => {
  if (date) {
    return new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      Number(time.split(':')[0]),
      Number(time.split(':')[1]),
      0
    )
  } else {
    const now = new Date()
    return new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      Number(time.split(':')[0]),
      Number(time.split(':')[1]),
      0
    )
  }
}

// xx.xx(hours) -> HH:mm
// 10.25 -> 10:15
export const parseNumberToTime = (hours: number): string => {
  const hour = Math.trunc(hours)
  let decimal = String(hours).split('.')[1]

  // 30分の場合、小数点以下の数値が1桁の"5"になるため2桁の"50"にする
  if (decimal === '5') decimal += '0'

  const time = decimal ? (Number(decimal) * 60) / 100 : 0
  return `${zeroPadding(hour)}:${zeroPadding(time)}`
}

// HH:mm -> xx.xx(hours)
// 10:15 -> 10.25
export const parseTimeToNumber = (time: string): number => {
  if (time === '') return 0
  const hours = Number(time.split(':')[0])
  const minutes = Number(time.split(':')[1])
  return hours + minutes / 60
}

// HH:mm -> H時
export const parseTimeToShortTime = (time: string): string => {
  if (!time || time.length === 0 || !/^[0-9]{2}:[0-9]{2}$/.test(time)) return ''
  const hour = time.split(':')[0]
  if (!hour) return ''
  // 十の位が0埋めされている場合、0を取り除く
  if (/^0.$/.test(hour)) {
    return hour.slice(1, 2)
  } else {
    return hour
  }
}

export const parseOrderType = (type: string): string => {
  switch (type) {
    case 'daily':
      return '当日取り置き'
    case 'preorder':
      return '事前予約'
    default:
      return ''
  }
}

export const isHolidayOfStore = (
  date: Date,
  holidays: DayOfWeekType[],
  specialHolidays: SpecialHolidayType[]
): boolean => {
  const dayOfWeek = daysOfWeek.getDayOfWeek(date, specialHolidays)
  return holidays.includes(dayOfWeek)
}

export const returnCodeToBr = (text: string): string => {
  if (!text || text === '') return ''
  return text.replace(/\r?\n/g, '<br/>')
}

export const sumCheckoutItemsAmount = (checkoutItems: CheckoutItemType[], pickupable_time?: string): number => {
  return checkoutItems.reduce((prev, checkoutItem) => {
    // 受渡時刻が指定されている場合、一致する受渡時刻であれば合算する
    if (pickupable_time) {
      if (checkoutItem.pickupable_time === pickupable_time) {
        return prev + checkoutItem.quantity
      } else {
        return prev
      }
      // 受渡時刻が指定されていなければ無条件で合算する
    } else {
      return prev + checkoutItem.quantity
    }
  }, 0)
}

export const sumStocksAmount = (stocks: StockType[], pickupable_time?: string): number => {
  return stocks.reduce((prev, stock) => {
    // 受渡時刻が指定されている場合、一致する受渡時刻であれば合算する
    if (pickupable_time) {
      if (stock.pickupable_time === pickupable_time) {
        return prev + stock.stock
      } else {
        return prev
      }
      // 受渡時刻が指定されていなければ無条件で合算する
    } else {
      return prev + stock.stock
    }
  }, 0)
}

// 2桁で0埋めした文字列を返す
export const zeroPadding = (number: number): string => {
  return ('00' + number).slice(-2)
}

// n日前を求める
export const parseDateToDeadline = (hoursToDeadline: number): string => {
  // 24で割り切れる場合は1日前にする必要がない
  if (hoursToDeadline % 24 === 0) {
    return String(Math.floor(hoursToDeadline / 24))
  } else {
    return String(Math.floor(hoursToDeadline / 24) + 1)
  }
}

// 締切時間(HH:mm)を求める
export const parseTimeToDeadline = (hoursToDeadline: number): string => {
  if (hoursToDeadline % 24 === 0) {
    // 24時間で割り切れるとき or 0時のときはそのまま0を渡す
    return parseNumberToTime(hoursToDeadline % 24)
  } else if (hoursToDeadline < 0) {
    // 当日の締切時刻は負の数になる
    return parseNumberToTime(-hoursToDeadline % 24)
  } else {
    // 締切なので、24時間から余りを引く、
    return parseNumberToTime(24 - (hoursToDeadline % 24))
  }
}

export const parseHoursToDeadline = (dateToDeadline: string, timeToDeadline: string): number => {
  return Number(dateToDeadline) * 24 - parseTimeToNumber(timeToDeadline)
}

export const parseBusinessHours = (handleBusinessHours: HandleOpenTimeType[]): OpenTimeType => {
  const businessHours: OpenTimeType = {}

  for (let i = 0; i < handleBusinessHours.length; i = (i + 1) | 0) {
    const handleBusinessHour = handleBusinessHours[i]

    for (let j = 0; j < handleBusinessHour.business_hours.length; j = (j + 1) | 0) {
      const businessHour = handleBusinessHour.business_hours[j]
      businessHours[businessHour] = {
        open_time: handleBusinessHour.open_time,
      }
    }
  }

  return businessHours
}

export const parseBusinessHoursToHandle = (businessHours: OpenTimeType): HandleOpenTimeType[] => {
  const handleBusinessHours: HandleOpenTimeType[] = []
  const daysOfWeek = Object.keys(businessHours)
  const values = Object.values(businessHours)

  for (let i = 0; i < daysOfWeek.length; i = (i + 1) | 0) {
    const dayOfWeek = daysOfWeek[i]
    const openTime = values[i].open_time
    const index = handleBusinessHours.findIndex((handleBusinessHour) => {
      return handleBusinessHour.open_time === openTime
    })

    if (index === -1) {
      // 受渡解禁時刻と閉店時刻のどちらかが一致しない場合は営業時間入力フォームのためのデータを1つ追加する
      handleBusinessHours.push({
        business_hours: [dayOfWeek as DayOfWeekType],
        open_time: openTime,
      })
    } else {
      // 曜日を追加する
      handleBusinessHours[index].business_hours.push(dayOfWeek as DayOfWeekType)
    }
  }

  return handleBusinessHours
}

export const parseStoreStatus = (status: string): string => {
  return (() => {
    switch (status) {
      case 'public':
        return '公開中'
      case 'private':
        return '下書き'
      case 'deleted':
        return '削除済'
      default:
        return ''
    }
  })()
}

export const parseTimestampToDatetime = (timestamp: FirebaseTimestampType): string => {
  return format(timestamp.toDate(), "yyyy-MM-dd'T'HH:mm")
}

export const parsePostalCodeWithHyphen = (postalCode: string): string => {
  return `${postalCode.slice(0, 3)}-${postalCode.slice(3, 7)}`
}

export const concatenateAddress = (address: AddressType): string => {
  return address.state + address.city + address.street + address.building
}

export const getNextStepMinutes = (date: Date): Date => {
  const minutes = date.getMinutes()
  if (minutes >= 0 && minutes < 15) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), 15, 0)
  } else if (minutes >= 15 && minutes < 30) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), 30, 0)
  } else if (minutes >= 30 && minutes < 45) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), 45, 0)
  } else {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours() + 1, 0, 0)
  }
}

export const parseHirakanaToKanakana = (hirakana: string): string => {
  if (!hirakana) return ''
  return hirakana.replace(/[\u3041-\u3096]/g, function (match) {
    const chr = match.charCodeAt(0) + 0x60
    return String.fromCharCode(chr)
  })
}

// 1,2,3...を①,②,③に変換
export const parseToCircleNumber = (num: number): string => {
  const base = '①'.charCodeAt(0)
  return String.fromCharCode(base + num - 1)
}

// 日付型をyyyy-MM-ddTHH:mm:ssZの形式に変換した後、yyyy-MM-ddTHH:mmのみ抽出する
export const parseDateToDatetimeLocal = (date: Date): string => {
  const isoDate = formatISO(date)
  return isoDate.slice(0, 16)
}

export const parseDaysToSeconds = (days: number): number => {
  return 24 * days * 60 * 60
}

export const parseSecondsToDays = (seconds: number): number => {
  return Math.floor(seconds / 24 / 60 / 60)
}

export const parseCampaignStatus = (
  periodStart: FirebaseTimestampType,
  periodEnd: FirebaseTimestampType,
  status: CampaignStatus
): string => {
  if (status === 'public') {
    const now = new Date()
    if (isAfter(now, periodStart.toDate()) && isBefore(now, periodEnd.toDate())) {
      return '公開中'
    } else {
      return '非公開'
    }
  } else {
    return '下書き'
  }
}

// WebViewで開いているかどうかの判定
export const getIsWebView = (): boolean => {
  const ua = window.navigator.userAgent.toLowerCase()

  if (window.location.host == 'localhost:3000' || window.location.host == '127.0.0.1:3000') {
    //PC開発用
    return true
  } else if (ua && ua.indexOf('webview_app') >= 0) {
    return true
  } else {
    return false
  }
}

// スマートフォンのデバイストークンを取得
export const getDeviceInfo = (): Promise<any> => {
  return new Promise((resolve) => {
    const plugin_param = {
      method: 'getDeviceInfo',
    }

    ;(window as any)._getDeviceInfoCallback = (res: any) => {
      resolve({
        device_token: res.device_token,
        device_type: res.platform,
      })
    }

    const webkit = (window as any).webkit
    const androidPlugin = (window as any).androidPlugin

    if (webkit && webkit.messageHandlers && webkit.messageHandlers.iosPlugin) {
      webkit.messageHandlers.iosPlugin.postMessage(JSON.stringify(plugin_param))
    } else if (androidPlugin) {
      androidPlugin.postMessage(JSON.stringify(plugin_param))
    } else {
      /*
      resolve({
        device_token:"XXXXXXXXXXXXXXXXXXXXXXXX",
        device_type:"pc"
      });
      */
      resolve(false)
    }
  })
}

export const parseFrozenDeliveryType = (frozenDeliveryType: FrozenDeliveryType): string => {
  switch (frozenDeliveryType) {
    case 'frozen':
      return 'クール便（冷凍）'
    case 'chilled':
      return 'クール便（冷蔵）'
    default:
      return ''
  }
}

export const parseDeliveryCompany = (deliveryCompany: string): string => {
  switch (deliveryCompany) {
    case 'yamato':
      return 'ヤマト運輸'
    case 'sagawa':
      return '佐川急便'
    default:
      return ''
  }
}

export const findBreadShortName = (breads: BreadType[], fullName: string, breadRef: FirebaseDocRefType): string => {
  const bread = breads.find((bread) => bread.bread_ref?.id === breadRef.id)
  if (!bread) return fullName
  return bread.short_name || fullName
}

export const getBreadShortName = (bread: BreadType): string => {
  return bread.short_name || bread.name
}

export const parsePhoneNumber = (phoneNumber: string): string => {
  return new AsYouType('JP').input(phoneNumber)
}

export const parseHankanaToZenkana = (word: string): string => {
  const kanaMap: { [key: string]: string } = {
    ｶﾞ: 'ガ',
    ｷﾞ: 'ギ',
    ｸﾞ: 'グ',
    ｹﾞ: 'ゲ',
    ｺﾞ: 'ゴ',
    ｻﾞ: 'ザ',
    ｼﾞ: 'ジ',
    ｽﾞ: 'ズ',
    ｾﾞ: 'ゼ',
    ｿﾞ: 'ゾ',
    ﾀﾞ: 'ダ',
    ﾁﾞ: 'ヂ',
    ﾂﾞ: 'ヅ',
    ﾃﾞ: 'デ',
    ﾄﾞ: 'ド',
    ﾊﾞ: 'バ',
    ﾋﾞ: 'ビ',
    ﾌﾞ: 'ブ',
    ﾍﾞ: 'ベ',
    ﾎﾞ: 'ボ',
    ﾊﾟ: 'パ',
    ﾋﾟ: 'ピ',
    ﾌﾟ: 'プ',
    ﾍﾟ: 'ペ',
    ﾎﾟ: 'ポ',
    ｳﾞ: 'ヴ',
    ﾜﾞ: 'ヷ',
    ｦﾞ: 'ヺ',
    ｱ: 'ア',
    ｲ: 'イ',
    ｳ: 'ウ',
    ｴ: 'エ',
    ｵ: 'オ',
    ｶ: 'カ',
    ｷ: 'キ',
    ｸ: 'ク',
    ｹ: 'ケ',
    ｺ: 'コ',
    ｻ: 'サ',
    ｼ: 'シ',
    ｽ: 'ス',
    ｾ: 'セ',
    ｿ: 'ソ',
    ﾀ: 'タ',
    ﾁ: 'チ',
    ﾂ: 'ツ',
    ﾃ: 'テ',
    ﾄ: 'ト',
    ﾅ: 'ナ',
    ﾆ: 'ニ',
    ﾇ: 'ヌ',
    ﾈ: 'ネ',
    ﾉ: 'ノ',
    ﾊ: 'ハ',
    ﾋ: 'ヒ',
    ﾌ: 'フ',
    ﾍ: 'ヘ',
    ﾎ: 'ホ',
    ﾏ: 'マ',
    ﾐ: 'ミ',
    ﾑ: 'ム',
    ﾒ: 'メ',
    ﾓ: 'モ',
    ﾔ: 'ヤ',
    ﾕ: 'ユ',
    ﾖ: 'ヨ',
    ﾗ: 'ラ',
    ﾘ: 'リ',
    ﾙ: 'ル',
    ﾚ: 'レ',
    ﾛ: 'ロ',
    ﾜ: 'ワ',
    ｦ: 'ヲ',
    ﾝ: 'ン',
    ｧ: 'ァ',
    ｨ: 'ィ',
    ｩ: 'ゥ',
    ｪ: 'ェ',
    ｫ: 'ォ',
    ｯ: 'ッ',
    ｬ: 'ャ',
    ｭ: 'ュ',
    ｮ: 'ョ',
    '｡': '。',
    '､': '、',
    ｰ: 'ー',
    '｢': '「',
    '｣': '」',
    '･': '・',
  }
  const reg = new RegExp('(' + Object.keys(kanaMap).join('|') + ')', 'g')
  return word
    .replace(reg, (match) => kanaMap[match])
    .replace(/ﾞ/g, '゛')
    .replace(/ﾟ/g, '゜')
}

export const parseHankakuToZenkaku = (word: string): string => {
  const zenkana = parseHankanaToZenkana(word)
  const zenkaku = zenkana.replace(/[A-Za-z0-9]/g, (s) => {
    return String.fromCharCode(s.charCodeAt(0) + 0xfee0)
  })
  return zenkaku
}

export const parseFrozenDeliveryTypeForYamato = (frozenDeliveryType: FrozenDeliveryType): string => {
  switch (frozenDeliveryType) {
    case '':
      return '0'
    case 'frozen':
      return '1'
    case 'chilled':
      return '2'
  }
}
